/**
 * Post-login warmup coordinator.
 *
 * Why this exists
 * ---------------
 * The "Conexão lenta detectada" banner on Dashboard and Flight Agenda is the
 * outward sign of a deeper UX problem: right after authentication, every
 * persistent cache is cold and the ECS backend may still be warming its
 * tenant connection. The first batch of XHRs each page fires can take
 * 8-15s to resolve even on a healthy LTE link, so the slow-load timers
 * fire even though the user is not actually on a constrained connection.
 *
 * This module solves that in three coordinated ways:
 *
 *   1. Record a `aviatto:last_login_at` timestamp the instant a session
 *      is installed (fresh login OR session install from token claim).
 *      Pages that emit slow-load banners read this timestamp and skip
 *      the banner while we are still inside the grace period.
 *
 *   2. Avoid post-login background data warmups. We briefly tried eagerly
 *      prefetching Dashboard/Agenda/Fuel/eDB data immediately after a session
 *      landed, but in production that competed with the operator's first real
 *      navigation: the shared GET queue admitted background requests before
 *      the page the operator actually clicked, which made Waitlist and other
 *      pages look broken. Post-login now records the grace-period signal only;
 *      route preloading stays in ProtectedAppRoutes where it runs after the
 *      authenticated shell has painted and still respects the API-prefetch
 *      feature flag.
 *
 *   3. Expose `isWithinLoginGracePeriod()` so any page-level slow-load
 *      timer can short-circuit its banner without re-implementing the
 *      same heuristic. Centralising the rule guarantees that every
 *      affected page (Dashboard, Flight Agenda, Checklist, etc.) stays
 *      in sync as the threshold evolves.
 *
 * The module is intentionally side-effect free at import time: callers
 * must invoke `markLoginCompleted()` to opt in. This keeps unit tests
 * simple and avoids accidental timestamp writes during SSR / preview
 * builds.
 */

const LAST_LOGIN_AT_KEY = 'aviatto:last_login_at';

// Grace window in milliseconds. Picked empirically from production
// observations:
//   * ECS task warm-up after a deploy: 3-6 s
//   * Auth refresh + first DB query against a cold tenant schema: 4-8 s
//   * First Dashboard Promise.all (5-6 XHRs + Suspense boundary): 4-8 s
// 30 s gives the foreground page a full cold-start window before any
// slow-load banner can fire, while still surfacing the banner if the
// user is genuinely stuck on a degraded connection.
export const LOGIN_GRACE_PERIOD_MS = 30 * 1000;

// Window we still consider "recent" for diagnostic and prefetch retry
// purposes. 5 minutes is well past the grace period but short enough
// that we never accidentally treat a multi-hour session as "fresh".
export const RECENT_LOGIN_WINDOW_MS = 5 * 60 * 1000;

function _safeNow() {
  try {
    return Date.now();
  } catch {
    return 0;
  }
}

function _safeGetItem(key) {
  try {
    if (typeof localStorage === 'undefined') return null;
    return localStorage.getItem(key);
  } catch {
    return null;
  }
}

function _safeSetItem(key, value) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.setItem(key, value);
  } catch {
    // best-effort; storage may be disabled or full.
  }
}

function _safeRemoveItem(key) {
  try {
    if (typeof localStorage === 'undefined') return;
    localStorage.removeItem(key);
  } catch {
    // ignore
  }
}

/**
 * Record that the current browser tab has a freshly-installed session.
 * Safe to call multiple times; later calls move the grace window forward
 * (e.g. when a tenant switch re-installs tokens mid-session).
 */
export function markLoginCompleted() {
  _safeSetItem(LAST_LOGIN_AT_KEY, String(_safeNow()));
}

/**
 * Reset the login timestamp on logout so subsequent navigations of an
 * unauthenticated visitor do not benefit from a stale grace period.
 */
export function clearLoginMark() {
  _safeRemoveItem(LAST_LOGIN_AT_KEY);
}

/**
 * Return the millisecond timestamp of the most recent login, or 0 when
 * no login has been recorded yet.
 */
export function getLastLoginAt() {
  const raw = _safeGetItem(LAST_LOGIN_AT_KEY);
  if (!raw) return 0;
  const parsed = Number(raw);
  return Number.isFinite(parsed) && parsed > 0 ? parsed : 0;
}

/**
 * True when we are still inside the post-login grace window. Pages use
 * this to skip slow-load banners that would otherwise fire just because
 * the user has not had time to populate the persistent SWR cache yet.
 *
 * The optional `now` argument exists so tests can pass a deterministic
 * clock instead of monkey-patching Date.now().
 */
export function isWithinLoginGracePeriod(now = _safeNow()) {
  const lastLoginAt = getLastLoginAt();
  if (!lastLoginAt) return false;
  return now - lastLoginAt < LOGIN_GRACE_PERIOD_MS;
}

/**
 * True when the user logged in within the broader "recent" window. Used
 * by warmup retry heuristics that should run a second time if the first
 * prefetch wave ran before the user identity scope was visible to the
 * cache key builder.
 */
export function isRecentLogin(now = _safeNow()) {
  const lastLoginAt = getLastLoginAt();
  if (!lastLoginAt) return false;
  return now - lastLoginAt < RECENT_LOGIN_WINDOW_MS;
}

/**
 * Returns the number of milliseconds remaining in the grace period.
 * Convenience for pages that want to extend their own loading timers
 * just enough to outlast the grace window without leaving the user
 * staring at a blank screen forever.
 */
export function remainingGracePeriodMs(now = _safeNow()) {
  const lastLoginAt = getLastLoginAt();
  if (!lastLoginAt) return 0;
  const elapsed = now - lastLoginAt;
  if (elapsed >= LOGIN_GRACE_PERIOD_MS) return 0;
  return Math.max(0, LOGIN_GRACE_PERIOD_MS - elapsed);
}

let warmupKicked = false;

/**
 * Backward-compatible hook for post-login work.
 *
 * This intentionally does NOT prefetch route data anymore. The foreground
 * page after login must own the network queue; otherwise a background warmup
 * can delay or starve the operator's first real page request. Chunk/data
 * preloading remains centralized in ProtectedAppRoutes and still honours
 * `VITE_ENABLE_API_PREFETCH`.
 */
export function kickPostLoginWarmup() {
  if (warmupKicked) return;
  warmupKicked = true;
}

export const __testing__ = {
  LAST_LOGIN_AT_KEY,
  resetWarmupFlag: () => { warmupKicked = false; },
};

export default {
  markLoginCompleted,
  clearLoginMark,
  getLastLoginAt,
  isWithinLoginGracePeriod,
  isRecentLogin,
  remainingGracePeriodMs,
  kickPostLoginWarmup,
  LOGIN_GRACE_PERIOD_MS,
  RECENT_LOGIN_WINDOW_MS,
};
